<template>
  <section class="fm-root">
    <div class="fm-toolbar">
      <div class="fm-breadcrumb">
        <button class="fm-crumb" :class="{ 'fm-crumb-active': !selectedProjectId }" type="button" @click="exitProject">
          Arquivos
        </button>
        <template v-if="selectedProject">
          <span class="fm-crumb-sep">›</span>
          <button class="fm-crumb" :class="{ 'fm-crumb-active': !currentFolderId }" type="button" @click="exitFolder">
            {{ selectedProject.name }}
          </button>
        </template>
        <template v-if="currentFolder">
          <span class="fm-crumb-sep">›</span>
          <span class="fm-crumb fm-crumb-active">{{ currentFolder.name }}</span>
        </template>
      </div>
      <div class="fm-toolbar-actions">
        <span v-if="!selectedProjectId" class="fm-count">{{ projectCards.length }} projeto{{ projectCards.length !== 1 ? 's' : '' }}</span>
        <span v-else class="fm-count">{{ visibleFiles.length }} arquivo{{ visibleFiles.length !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <p v-if="errorText" class="fm-error-inline">{{ errorText }}</p>
    <div v-else-if="loading" class="fm-empty">
      <div class="fm-empty-icon">⏳</div>
      <p>Carregando arquivos...</p>
    </div>
    <div v-else-if="!selectedProjectId && projectCards.length === 0" class="fm-empty">
      <div class="fm-empty-icon">📁</div>
      <p>Nenhum projeto disponível para visualizar arquivos.</p>
    </div>
    <div v-else-if="selectedProjectId && gridItems.length === 0" class="fm-empty">
      <div class="fm-empty-icon">{{ currentFolderId ? '📂' : '📁' }}</div>
      <p>{{ currentFolderId ? 'Esta pasta está vazia.' : 'Nenhum arquivo neste projeto.' }}</p>
    </div>

    <div v-else class="fm-grid">
      <template v-if="!selectedProjectId">
        <div
          v-for="project in projectCards"
          :key="project.id"
          class="fm-card fm-card--folder fm-card--project"
          @click="enterProject(project.id)"
        >
          <div class="fm-card-thumb fm-thumb-folder fm-thumb-project">
            <img v-if="project.photoDataUrl" class="fm-project-thumb" :src="$resolveUrl(project.photoDataUrl)" alt="" />
            <svg v-else width="38" height="30" viewBox="0 0 38 30" fill="none">
              <rect x="0" y="6" width="38" height="24" rx="3" fill="currentColor" opacity="0.25"/>
              <rect x="0" y="3" width="16" height="6" rx="2" fill="currentColor" opacity="0.4"/>
              <rect x="0" y="6" width="38" height="24" rx="3" fill="currentColor" opacity="0.15"/>
            </svg>
          </div>
          <div class="fm-card-info">
            <div class="fm-card-name" :title="project.name">{{ project.name }}</div>
            <div class="fm-card-meta">
              <span class="fm-card-type">{{ project.filesCount }} arquivo{{ project.filesCount !== 1 ? 's' : '' }}</span>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div
          v-for="folder in visibleFolders"
          :key="'f-' + folder.id"
          class="fm-card fm-card--folder"
          @click="enterFolder(folder)"
        >
          <div class="fm-card-thumb fm-thumb-folder">
            <svg width="38" height="30" viewBox="0 0 38 30" fill="none">
              <rect x="0" y="6" width="38" height="24" rx="3" fill="currentColor" opacity="0.25"/>
              <rect x="0" y="3" width="16" height="6" rx="2" fill="currentColor" opacity="0.4"/>
              <rect x="0" y="6" width="38" height="24" rx="3" fill="currentColor" opacity="0.15"/>
            </svg>
          </div>
          <div class="fm-card-info">
            <div class="fm-card-name" :title="folder.name">{{ folder.name }}</div>
            <div class="fm-card-meta">
              <span class="fm-card-type">Pasta</span>
            </div>
          </div>
        </div>

        <div
          v-for="item in visibleFiles"
          :key="'file-' + item.id"
          class="fm-card fm-card--file"
          @click="openViewer(item)"
        >
          <div class="fm-card-thumb">
            <img
              v-if="item.thumbnailBase64"
              class="fm-thumb-img"
              :src="item.thumbnailBase64.startsWith('/uploads/') ? $resolveUrl(item.thumbnailBase64) : `data:image/jpeg;base64,${item.thumbnailBase64}`"
              alt=""
            />
            <div v-else class="fm-file-icon" :class="`fm-icon-${item.extension.toLowerCase()}`">
              <span class="fm-icon-ext">{{ item.extension.toUpperCase() }}</span>
            </div>
          </div>
          <div class="fm-card-info">
            <div class="fm-card-name" :title="item.name">{{ item.name }}</div>
            <div class="fm-card-meta">
              <span class="fm-badge-rev">{{ item.revision || 'R0' }}</span>
              <span class="fm-card-type">{{ item.extension.toUpperCase() }}</span>
            </div>
          </div>
          <div class="fm-card-hover" @click.stop>
            <button class="fm-action-btn" title="Visualizar" @click="openViewer(item)">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3C4.5 3 1.5 8 1.5 8s3 5 6.5 5 6.5-5 6.5-5-3-5-6.5-5zm0 8a3 3 0 110-6 3 3 0 010 6z" fill="currentColor"/><circle cx="8" cy="8" r="1.5" fill="currentColor"/></svg>
            </button>
          </div>
        </div>
      </template>
    </div>

    <Teleport to="body">
      <Transition name="viewer-fade">
        <div v-if="viewerFile" class="fm-viewer-overlay" @click.self="closeViewer">
          <div class="fm-viewer-modal">
            <div class="fm-viewer-header">
              <div class="fm-viewer-title-group">
                <div class="fm-file-icon fm-file-icon-sm" :class="`fm-icon-${viewerFile.extension.toLowerCase()}`">
                  <span class="fm-icon-ext">{{ viewerFile.extension.toUpperCase() }}</span>
                </div>
                <div>
                  <div class="fm-viewer-name">{{ viewerFile.name }}</div>
                  <div class="fm-viewer-meta">{{ viewerFile.extension.toUpperCase() }} · {{ viewerFile.revision || 'R0' }}{{ viewerFile.description ? ' · ' + viewerFile.description : '' }}</div>
                </div>
              </div>
              <div class="fm-viewer-controls">
                <button class="fm-viewer-close" type="button" @click="closeViewer">✕</button>
              </div>
            </div>
            <div class="fm-viewer-body">
              <iframe
                v-if="viewerFile.extension === 'pdf'"
                class="fm-iframe"
                :src="viewerFile.url ? $resolveUrl(viewerFile.url) : `data:${viewerFile.mimeType};base64,${viewerFile.contentBase64}`"
              />
              <div v-else-if="viewerFile.extension === 'dwg'" class="fm-dwg-placeholder">
                <div class="fm-dwg-big">📐</div>
                <p>Arquivo DWG</p>
                <p class="fm-viewer-meta">Visualização DWG não disponível no navegador.</p>
              </div>
              <img
                v-else
                class="fm-img-viewer"
                :src="viewerFile.url ? $resolveUrl(viewerFile.url) : `data:${viewerFile.mimeType};base64,${viewerFile.contentBase64}`"
                alt="preview"
              />
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch, type PropType } from 'vue'
import { apiFetch } from '@/store/api'
import type { Project } from '@/store/projects'

interface ProjectFile {
  id: string
  name: string
  description?: string
  revision?: string
  mimeType: string
  extension: string
  contentBase64?: string
  url?: string
  folderId?: string | null
  thumbnailBase64?: string | null
}

interface ProjectFolder {
  id: string
  name: string
  createdAt: string
}

interface ProjectFileBundle {
  files: ProjectFile[]
  folders: ProjectFolder[]
}

export default defineComponent({
  name: 'DrawingsPanel',
  props: {
    projects: {
      type: Array as PropType<Project[]>,
      default: () => []
    }
  },
  setup(props) {
    const loading = ref(false)
    const errorText = ref('')
    const selectedProjectId = ref('')
    const currentFolderId = ref<string | null>(null)
    const viewerFile = ref<ProjectFile | null>(null)
    const projectFileMap = ref<Record<string, ProjectFileBundle>>({})

    const projectCards = computed(() =>
      props.projects.map((project) => ({
        id: project.id,
        name: project.name,
        photoDataUrl: project.photoDataUrl || '',
        filesCount: projectFileMap.value[project.id]?.files?.length || 0
      }))
    )

    const selectedProject = computed(() =>
      props.projects.find((project) => project.id === selectedProjectId.value) || null
    )

    const currentBundle = computed<ProjectFileBundle>(() =>
      projectFileMap.value[selectedProjectId.value] || { files: [], folders: [] }
    )

    const visibleFolders = computed(() =>
      currentFolderId.value ? [] : currentBundle.value.folders
    )

    const visibleFiles = computed(() =>
      currentBundle.value.files.filter((file) =>
        currentFolderId.value ? file.folderId === currentFolderId.value : !file.folderId
      )
    )

    const currentFolder = computed(() =>
      currentBundle.value.folders.find((folder) => folder.id === currentFolderId.value) || null
    )

    const gridItems = computed(() => [...visibleFolders.value, ...visibleFiles.value])

    const refreshFiles = async () => {
      loading.value = true
      errorText.value = ''
      try {
        const entries = await Promise.all(
          props.projects.map(async (project) => {
            const data = await apiFetch<ProjectFileBundle>(`/projects/${encodeURIComponent(project.id)}/files`)
            return [project.id, { files: data.files || [], folders: data.folders || [] }] as const
          })
        )
        projectFileMap.value = Object.fromEntries(entries)
      } catch {
        errorText.value = 'Falha ao carregar arquivos dos projetos.'
      } finally {
        loading.value = false
      }
    }

    const enterProject = (projectId: string) => {
      selectedProjectId.value = projectId
      currentFolderId.value = null
      viewerFile.value = null
    }

    const exitProject = () => {
      selectedProjectId.value = ''
      currentFolderId.value = null
      viewerFile.value = null
    }

    const enterFolder = (folder: ProjectFolder) => {
      currentFolderId.value = folder.id
      viewerFile.value = null
    }

    const exitFolder = () => {
      currentFolderId.value = null
    }

    const openViewer = async (item: ProjectFile) => {
      viewerFile.value = await apiFetch<ProjectFile>(`/project-files/${encodeURIComponent(item.id)}/content`)
    }

    const closeViewer = () => {
      viewerFile.value = null
    }

    watch(
      () => props.projects.map((project) => project.id).join('|'),
      () => {
        if (selectedProjectId.value && !props.projects.some((project) => project.id === selectedProjectId.value)) {
          exitProject()
        }
        refreshFiles().catch(() => {
          // keep panel interactive if refresh fails
        })
      }
    )

    onMounted(() => {
      refreshFiles().catch(() => {
        // keep panel interactive if initial fetch fails
      })
    })

    return {
      loading,
      errorText,
      selectedProjectId,
      selectedProject,
      currentFolderId,
      currentFolder,
      projectCards,
      visibleFolders,
      visibleFiles,
      gridItems,
      viewerFile,
      enterProject,
      exitProject,
      enterFolder,
      exitFolder,
      openViewer,
      closeViewer
    }
  }
})
</script>

<style scoped lang="scss">
.fm-root {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.fm-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.fm-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.fm-crumb {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: #7c6753;
  cursor: pointer;
}

.fm-crumb-active {
  color: #2c2b27;
}

.fm-crumb-sep {
  color: #b9ad9d;
}

.fm-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.fm-count {
  font-size: 13px;
  color: #7c6753;
  font-weight: 600;
}

.fm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}

.fm-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(31, 30, 26, 0.1);
  background: linear-gradient(180deg, #fffdf8 0%, #f8f4ed 100%);
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.fm-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 36px rgba(71, 53, 27, 0.08);
  border-color: rgba(47, 111, 206, 0.18);
}

.fm-card-thumb {
  height: 132px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: linear-gradient(180deg, #f4efe6 0%, #efe7da 100%);
  color: #9b7b47;
}

.fm-thumb-folder {
  background: linear-gradient(180deg, #fbf5e8 0%, #f0e2bf 100%);
}

.fm-thumb-project {
  padding: 0;
}

.fm-project-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.fm-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.fm-card-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.fm-card-name {
  font-size: 14px;
  font-weight: 700;
  color: #2c2b27;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fm-card-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.fm-card-type {
  font-size: 11px;
  color: #7c6753;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.fm-badge-rev {
  padding: 3px 7px;
  border-radius: 999px;
  background: rgba(47, 111, 206, 0.1);
  color: #2f6fce;
  font-size: 11px;
  font-weight: 700;
}

.fm-file-icon {
  width: 82px;
  height: 96px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(31, 30, 26, 0.08);
  display: grid;
  place-items: center;
  box-shadow: 0 8px 20px rgba(53, 43, 30, 0.08);
}

.fm-file-icon-sm {
  width: 42px;
  height: 50px;
  border-radius: 10px;
}

.fm-icon-ext {
  font-size: 14px;
  font-weight: 800;
  color: #7c6753;
  text-transform: uppercase;
}

.fm-card-hover {
  position: absolute;
  top: 10px;
  right: 10px;
  opacity: 0;
  transition: opacity 0.12s ease;
}

.fm-card:hover .fm-card-hover {
  opacity: 1;
}

.fm-action-btn {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  background: rgba(255, 255, 255, 0.92);
  color: #4a4036;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(53, 43, 30, 0.12);
}

.fm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 320px;
  border: 1px dashed rgba(31, 30, 26, 0.14);
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(255, 251, 243, 0.9) 0%, rgba(247, 240, 229, 0.95) 100%);
  text-align: center;
  color: #7c6753;
}

.fm-empty-icon {
  font-size: 32px;
}

.fm-error-inline {
  margin: 0;
  color: #b3472a;
  font-size: 13px;
  font-weight: 600;
}

.fm-viewer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.7);
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 20px;
}

.fm-viewer-modal {
  width: min(1040px, 100%);
  max-height: 90vh;
  border-radius: 22px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.35);
}

.fm-viewer-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(31, 30, 26, 0.08);
}

.fm-viewer-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.fm-viewer-name {
  font-size: 17px;
  font-weight: 800;
  color: #201b17;
}

.fm-viewer-meta {
  font-size: 12px;
  color: #7c6753;
}

.fm-viewer-controls {
  display: flex;
  align-items: center;
}

.fm-viewer-close {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 999px;
  background: rgba(31, 30, 26, 0.06);
  cursor: pointer;
  font-size: 16px;
}

.fm-viewer-body {
  padding: 18px;
  max-height: calc(90vh - 78px);
  overflow: auto;
}

.fm-iframe {
  width: 100%;
  min-height: 70vh;
  border: none;
  border-radius: 16px;
  background: #f7f7f7;
}

.fm-img-viewer {
  width: 100%;
  max-height: 72vh;
  object-fit: contain;
  border-radius: 16px;
  background: #f8fafc;
}

.fm-dwg-placeholder {
  min-height: 320px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  gap: 4px;
  background: linear-gradient(180deg, #faf7ef 0%, #f3e6cd 100%);
  color: #7c6753;
  text-align: center;
}

.fm-dwg-big {
  font-size: 48px;
}

.viewer-fade-enter-active,
.viewer-fade-leave-active {
  transition: opacity 0.18s ease;
}

.viewer-fade-enter-from,
.viewer-fade-leave-to {
  opacity: 0;
}

@media (max-width: 760px) {
  .fm-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }

  .fm-card-thumb {
    height: 110px;
  }

  .fm-viewer-header {
    padding: 14px;
  }

  .fm-viewer-body {
    padding: 12px;
  }
}
</style>
